import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Typography } from "@mui/material"
import React, { ChangeEvent, useCallback, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../../../../../theme"
import CancelButton from "../../../../../../../components/buttons/CancelButton/CancelButton"
import { SubmitButton } from "../../../../../../../components/buttons/SubmitButton/SubmitButton"
import { Transition } from "../../../../../../../components/dialog/BaseDialog"
import { SwitchLabelTypography } from "../../../../../../../components/typography/switch-label-typography"
import { pagesUrl } from "../../../../../../../core/appConstants"
import { BSVariantContext } from "../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { ProjectContext } from "../../../../../../../core/context/project/ProjectContext"
import { BSBuilding } from "../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { BSBuildingCreation } from "../../../../../../../core/dto/beem-shot/BSBuilding/BSBuildingCreation"
import { useForm } from "../../../../../../../core/hooks/form/use-form"
import { required } from "../../../../../../../core/hooks/form/validation"
import { resolveUrl } from "../../../../../../../core/services/http-service"
import { BSBuildingInfosForm } from "../../tabs/BSBuildingTabs/BSBuildingInfosTab/components/BSBuildingInfosForm"
import { BSBuildingContext } from "./context/BSBuildingContext"

const formId = "create-building-form"

interface IProps {
  open: boolean

  handleClose(): void
}

interface IForm {
  name: string
  description: string
  bim: boolean
}

function formToCreationDto(form: IForm, projectId: string, bsVariantId: string): BSBuildingCreation {
  return {
    bsVariantId,
    projectId,
    name: form.name.trim(),
    description: form.description,
    bim: form.bim,
  }
}

function dtoToForm(bsBuilding: BSBuilding | undefined): IForm {
  return bsBuilding
    ? {
        name: bsBuilding.name,
        description: bsBuilding.description,
        bim: bsBuilding.bim,
      }
    : {
        name: "",
        description: "",
        bim: false,
      }
}
export function BSBuildingFormDialog({ open, handleClose }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()

  const { project } = useContext(ProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { selectedBSBuilding, createNewBSBuilding } = useContext(BSBuildingContext)

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form) => {
      if (selectedBSBuilding?.id) {
        navigate(resolveUrl(pagesUrl.BUILDING_CHOICE, [project?.id, selectedVariant?.id, selectedBSBuilding?.id]))
        return Promise.resolve()
      } else if (project?.id && selectedVariant?.id) {
        return createNewBSBuilding(formToCreationDto(form, project?.id, selectedVariant?.id)).then((newBSBuilding) =>
          navigate(resolveUrl(pagesUrl.BUILDING_CHOICE, [project.id, selectedVariant.id, newBSBuilding.id]))
        )
      }

      return Promise.resolve()
    },
    [createNewBSBuilding, navigate, project?.id, selectedBSBuilding, selectedVariant?.id]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting } = useForm(
    selectedBSBuilding,
    dtoToForm,
    [required("name")],
    submit
  )

  function onChangeSwitch(event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void {
    const e: ChangeEvent<HTMLInputElement> = {
      target: {
        id: event.target.id,
        checked,
        type: "checkbox",
      },
    } as ChangeEvent<HTMLInputElement>
    handleChange(e)
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: customPalette.white,
        },
      }}
      TransitionComponent={Transition}>
      <Box>
        <DialogTitle variant="subtitle2">
          <Typography variant="h5" fontWeight={600}>
            Informations
          </Typography>
        </DialogTitle>

        <DialogContent>
          <BSBuildingInfosForm
            formId={formId}
            form={form}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />

          <Typography variant="body1" fontWeight={600} sx={{ color: customPalette.darkBlue2, mt: 1 }}>
            Morphologie
          </Typography>
          <FormControlLabel
            control={<Switch id="bim" checked={form.bim} onChange={onChangeSwitch} />}
            label={<SwitchLabelTypography label="Ce bâtiment possède une maquette Beem." />}
          />
        </DialogContent>
      </Box>

      <DialogActions>
        <CancelButton onClick={handleClose} label="Annuler" />
        <SubmitButton label="Créer" formId={formId} isSubmitting={isSubmitting} />
      </DialogActions>
    </Dialog>
  )
}
